import { useCallback, useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  Flex,
  Image,
  Portal,
  Text,
  Textarea
} from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'

import IconClose from '@/components/icons/IconClose'
import { useEditDesign } from '@/api/edit-design'
import { useUploadGraphics } from '@/hooks/use-upload-graphics'
import { useContentModeration } from '@/hooks/use-content-moderation'
import { containsSensitiveContent } from '@/utils/check-sensitive-content'
import { useBrandContext } from '@/context/brand-context'
import GenerationAnimation from './generation-animation'

import type { Design } from '@/api/designs'
import type { GraphicItem } from '@/context/brand-context'
import type { EditDesignBody } from '@/api/edit-design'

const MAX_GRAPHICS = 3

interface EditDesignModalProps {
  design: Design
  isOpen: boolean
  onClose: () => void
  onComplete: () => void
}

export default function EditDesignModal({
  design,
  isOpen,
  onClose,
  onComplete
}: EditDesignModalProps) {
  const [graphics, setGraphics] = useState<GraphicItem[]>([])
  const [prompt, setPrompt] = useState('')
  const [editing, setEditing] = useState(false)
  const [editError, setEditError] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { brandAnalysis } = useBrandContext()
  const { mutateAsync: editDesign } = useEditDesign()
  const uploadGraphics = useUploadGraphics()

  const { isFlagged } = useContentModeration(prompt)

  const brandName = brandAnalysis?.brand.name ?? 'Brand'

  const handleFileUpload = useCallback(
    (files: FileList | null) => {
      if (!files) return

      const slotsLeft = MAX_GRAPHICS - graphics.length

      const newItems: GraphicItem[] = Array.from(files)
        .slice(0, slotsLeft)
        .map((file) => ({
          type: 'uploaded' as const,
          url: URL.createObjectURL(file),
          name: file.name,
          file
        }))

      setGraphics([...graphics, ...newItems])
    },
    [graphics]
  )

  const removeGraphic = (index: number) => {
    const updated = [...graphics]
    const removed = updated.splice(index, 1)[0]
    if (removed.url.startsWith('blob:')) {
      URL.revokeObjectURL(removed.url)
    }
    setGraphics(updated)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    handleFileUpload(e.dataTransfer.files)
  }

  const lastBodyRef = useRef<EditDesignBody | null>(null)

  const runEdit = useCallback(
    async (body: EditDesignBody) => {
      setEditing(true)
      setEditError(false)
      lastBodyRef.current = body

      try {
        let subjectImageUrls: string[] | undefined

        if (graphics.length > 0) {
          subjectImageUrls = await uploadGraphics(graphics)
        }

        await editDesign({
          designId: design.id,
          body: {
            ...body,
            ...(subjectImageUrls && { subjectImageUrls })
          }
        })

        onComplete()
      } catch {
        setEditError(true)
      }
    },
    [design.id, graphics, uploadGraphics, editDesign, onComplete]
  )

  const handleEdit = () => {
    if (!prompt.trim() || containsSensitiveContent(prompt)) return

    const body: EditDesignBody = {
      prompt: prompt.trim()
    }

    runEdit(body)
  }

  const handleRetry = () => {
    if (lastBodyRef.current) {
      runEdit(lastBodyRef.current)
    }
  }

  const handleOpenChange = (details: { open: boolean }) => {
    if (!details.open) {
      if (editing && !editError) return
      onClose()
    }
  }

  const canClose = !editing || editError
  const isDisabled = !prompt.trim() || isFlagged

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={handleOpenChange}
      placement="center"
    >
      <Portal>
        <Dialog.Backdrop bg="blackAlpha.700" />
        <Dialog.Positioner>
          <Dialog.Content
            maxW="600px"
            w="full"
            mx="16px"
            maxH="90vh"
            bg="bg.primary"
            border="1px solid"
            borderColor="border.subtle"
            borderRadius="20px"
            p="0"
            position="relative"
            overflow="hidden"
          >
            <Flex
              justify="space-between"
              align="center"
              px="32px"
              pt="28px"
              pb="20px"
            >
              <Dialog.Title asChild>
                <Text
                  textStyle="subtitle"
                  color="text.primary"
                  fontWeight={400}
                >
                  Edit Design
                </Text>
              </Dialog.Title>
              {canClose && (
                <Dialog.CloseTrigger asChild>
                  <Box cursor="pointer" onClick={onClose}>
                    <IconClose boxSize="18px" color="text.tertiary" />
                  </Box>
                </Dialog.CloseTrigger>
              )}
            </Flex>

            <Dialog.Body
              px="32px"
              pb="32px"
              pt="0"
              overflowY="auto"
              maxH="calc(90vh - 80px)"
              css={{
                '&::-webkit-scrollbar': { width: '3px' },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(255,255,255,0.14)',
                  borderRadius: '4px'
                }
              }}
            >
              {editing ? (
                editError ? (
                  <Box textAlign="center" py="48px">
                    <Text color="text.secondary" textStyle="body" mb="24px">
                      Something went wrong generating designs. Let&apos;s try
                      again.
                    </Text>
                    <Button
                      h="48px"
                      px="32px"
                      textStyle="body"
                      fontWeight={500}
                      borderRadius="10px"
                      bg="accent.500"
                      color="white"
                      _hover={{
                        bg: 'accent.400',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 0 30px rgba(0, 112, 243, 0.3)'
                      }}
                      transition="all 0.3s ease"
                      onClick={handleRetry}
                    >
                      Retry
                    </Button>
                  </Box>
                ) : (
                  <GenerationAnimation brandName={brandName} compact />
                )
              ) : (
                <>
                  <Flex justify="center" mb="20px">
                    <Image
                      src={design.imageUrl}
                      alt={design.name}
                      w="250px"
                      h="250px"
                      objectFit="cover"
                      borderRadius="16px"
                      border="1px solid"
                      borderColor="rgba(255,255,255,0.12)"
                    />
                  </Flex>

                  <Box mb="20px">
                    <Text color="text.secondary" textStyle="label" mb="8px">
                      Graphics
                    </Text>

                    <Flex gap="10px" mb="10px" flexWrap="wrap">
                      <AnimatePresence>
                        {graphics.map((graphic, index) => (
                          <motion.div
                            key={graphic.url}
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                          >
                            <Box
                              w="80px"
                              h="80px"
                              borderRadius="10px"
                              border="1px solid"
                              borderColor="accent.500"
                              overflow="hidden"
                              position="relative"
                              bg="bg.secondary"
                            >
                              <Image
                                src={graphic.url}
                                alt={graphic.name}
                                w="100%"
                                h="100%"
                                objectFit="cover"
                              />
                              <Box
                                position="absolute"
                                top="4px"
                                right="4px"
                                w="18px"
                                h="18px"
                                borderRadius="full"
                                bg="bg.primary"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                cursor="pointer"
                                _hover={{ bg: 'negative' }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  removeGraphic(index)
                                }}
                              >
                                <IconClose
                                  boxSize="10px"
                                  color="text.primary"
                                />
                              </Box>
                            </Box>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </Flex>

                    <Text
                      color="text.tertiary"
                      textStyle="bodySuperSmall"
                      mb="10px"
                    >
                      {graphics.length} of 3 graphics
                    </Text>

                    {graphics.length < MAX_GRAPHICS && (
                      <Box
                        w="100%"
                        h="72px"
                        border="2px dashed"
                        borderColor="border.subtle"
                        borderRadius="14px"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        cursor="pointer"
                        transition="all 0.3s ease"
                        _hover={{
                          borderColor: 'accent.500',
                          bg: 'rgba(0, 112, 243, 0.03)',
                          boxShadow: '0 0 30px rgba(0, 112, 243, 0.08) inset'
                        }}
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                      >
                        <Text
                          color="text.secondary"
                          textStyle="bodySmall"
                          mb="2px"
                        >
                          Add graphics
                        </Text>
                        <Text color="text.tertiary" textStyle="bodySuperSmall">
                          PNG, JPG, SVG, WebP (up to{' '}
                          {MAX_GRAPHICS - graphics.length} more)
                        </Text>
                      </Box>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      multiple
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileUpload(e.target.files)}
                    />
                  </Box>

                  <Box mb="24px">
                    <Text color="text.secondary" textStyle="label" mb="8px">
                      Edit Prompt
                    </Text>
                    <Textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="Describe how you want to edit this design..."
                      bg="bg.secondary"
                      border="1px solid"
                      borderColor={isFlagged ? 'red.500' : 'border.subtle'}
                      borderRadius="12px"
                      color="text.primary"
                      _placeholder={{ color: 'text.tertiary' }}
                      _hover={{ borderColor: isFlagged ? 'red.500' : 'border.strong' }}
                      _focus={{
                        borderColor: isFlagged ? 'red.500' : 'accent.500',
                        boxShadow: isFlagged
                          ? '0 0 0 1px var(--chakra-colors-red-500)'
                          : '0 0 0 1px var(--chakra-colors-accent-500)'
                      }}
                      transition="border-color 0.2s ease"
                      resize="vertical"
                      minH="90px"
                      p="14px"
                    />
                    {isFlagged && (
                      <Text textStyle="bodySuperSmall" color="red.400" mt="6px">
                        Your prompt contains content that violates our usage policy.
                      </Text>
                    )}
                  </Box>

                  <Button
                    w="100%"
                    h="48px"
                    textStyle="body"
                    fontWeight={500}
                    borderRadius="10px"
                    bg={isDisabled ? 'bg.surface' : 'accent.500'}
                    color={isDisabled ? 'text.tertiary' : 'white'}
                    cursor={isDisabled ? 'not-allowed' : 'pointer'}
                    border="1px solid"
                    borderColor={isDisabled ? 'border.subtle' : 'accent.500'}
                    transition="all 0.3s ease"
                    _hover={
                      isDisabled
                        ? {}
                        : {
                            bg: 'accent.400',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 0 30px rgba(0, 112, 243, 0.3)'
                          }
                    }
                    onClick={isDisabled ? undefined : handleEdit}
                  >
                    Edit Design
                  </Button>
                </>
              )}
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
